import React from 'react';
import DynamicDataTooltip from '../DynamicDataTooltip/DynamicDataTooltip';

interface PatchStatusBarProps {
  completed: number;
  inProgress: number;
  errors: number;
  unknown: number;
  // This specific patch's own device count (how many agents actually target it, per its
  // AgentRemediationTaskDetail data) - NOT the whole plan's agent count. Different patches can target
  // different subsets of devices, so each patch's bars are a share of its own count, not the plan's.
  totalDevices: number;
}

// Four separate progress bars (Completed / In Progress / Errors / Unknown), each showing that bucket's
// count as a share of this patch's own device count.
const PatchStatusBar: React.FC<PatchStatusBarProps> = ({ completed, inProgress, errors, unknown, totalDevices }) => {
  const segments = [
    { key: 'completed', label: 'Completed', count: completed, color: '#15803d' },
    { key: 'inProgress', label: 'In Progress', count: inProgress, color: '#2563eb' },
    { key: 'errors', label: 'Errors', count: errors, color: '#b91c1c' },
    { key: 'unknown', label: 'Unknown', count: unknown, color: '#ca8a04' },
  ];

  return (
    <div className="grid grid-cols-2 gap-x-3 gap-y-1">
      {segments.map((segment) => {
        const pct = totalDevices > 0 ? Math.min(100, (segment.count / totalDevices) * 100) : 0;
        return (
          <DynamicDataTooltip
            key={segment.key}
            data={{ [segment.label]: segment.count, 'Total Devices': totalDevices }}
            fieldLabels={{ [segment.label]: segment.label, 'Total Devices': 'Total Devices' }}
          >
            {/* DynamicDataTooltip wraps children in an inline-flex (shrink-to-fit) box, so this row
                needs its own explicit width for flex-1 below to have space to grow into. Count is
                shown on hover via the tooltip data above, not inline. */}
            <div className="flex items-center gap-1.5 w-32">
              <span className="text-[10px] text-gray-400 w-14 shrink-0 truncate">{segment.label}</span>
              <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#333' }}>
                {pct > 0 && <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: segment.color }} />}
              </div>
            </div>
          </DynamicDataTooltip>
        );
      })}
    </div>
  );
};

export default PatchStatusBar;
